import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PvpSyncMetric, PvpSyncText } from '@/types/pvp';
import {
  METRIC_HELP,
  TEXT_HELP,
  fmtNum,
  keysCutNote,
  sortMetrics,
  sortTexts,
  textEntries,
} from './model';

const { Text } = Typography;
const small = { fontSize: 12 } as const;

const CutNote: React.FC<{ cut?: number }> = ({ cut }) => {
  const note = keysCutNote(cut);
  return note ? (
    <Text type="secondary" style={{ ...small, display: 'block', marginTop: 4 }}>
      {note}
    </Text>
  ) : null;
};

const KeyCell: React.FC<{ name: string; help?: string }> = ({ name, help }) => (
  <div>
    <Text code>{name}</Text>
    {help && (
      <div>
        <Text type="secondary" style={small}>
          {help}
        </Text>
      </div>
    )}
  </div>
);

const quantile = (
  field: 'p50' | 'p95' | 'p99' | 'min' | 'max'
): ColumnsType<PvpSyncMetric>[number] => ({
  title: field,
  key: field,
  align: 'right',
  render: (_, m) => fmtNum(m[field]),
});

const metricColumns: ColumnsType<PvpSyncMetric> = [
  {
    title: 'Metric (ms)',
    key: 'key',
    render: (_, m) => <KeyCell name={m.key} help={METRIC_HELP[m.key]} />,
  },
  { title: 'Role', dataIndex: 'role', key: 'role' },
  { title: 'Samples', key: 'n', align: 'right', render: (_, m) => fmtNum(m.n, 0) },
  quantile('p50'),
  quantile('p95'),
  quantile('p99'),
  quantile('min'),
  quantile('max'),
];

const textColumns: ColumnsType<PvpSyncText> = [
  {
    title: 'Text value',
    key: 'key',
    render: (_, t) => <KeyCell name={t.key} help={TEXT_HELP[t.key]} />,
  },
  { title: 'Role', dataIndex: 'role', key: 'role' },
  {
    title: 'Seen',
    key: 'values',
    render: (_, t) => {
      const entries = textEntries(t.values);
      const total = entries.reduce((sum, e) => sum + e.count, 0);
      return entries.map(({ label, count }) => (
        <Tag key={label}>
          {label}: {fmtNum(count, 0)}
          {total > 0 ? ` (${fmtNum((count / total) * 100, 0)}%)` : ''}
        </Tag>
      ));
    },
  },
];

/** Per-metric percentiles split by role, known keys first with what each means. */
export const MetricTable: React.FC<{
  metrics: PvpSyncMetric[];
  texts: PvpSyncText[];
  keysCut?: { metrics: number; texts: number };
}> = ({ metrics, texts, keysCut }) => (
  <>
    <Table<PvpSyncMetric>
      size="small"
      pagination={false}
      rowKey={(m) => `${m.key}/${m.role}`}
      columns={metricColumns}
      dataSource={sortMetrics(metrics)}
      scroll={{ x: true }}
    />
    <CutNote cut={keysCut?.metrics} />
    {texts.length > 0 && (
      <Table<PvpSyncText>
        size="small"
        pagination={false}
        style={{ marginTop: 12 }}
        rowKey={(t) => `${t.key}/${t.role}`}
        columns={textColumns}
        dataSource={sortTexts(texts)}
        scroll={{ x: true }}
      />
    )}
    <CutNote cut={keysCut?.texts} />
  </>
);
